import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const DependencyMapModal = ({ isOpen, onClose, task, allTasks, onSave }) => {
  const [dependencies, setDependencies] = useState(task?.dependencies || []);
  const [dependencyType, setDependencyType] = useState('finish-to-start');
  const [lagTime, setLagTime] = useState(0);

  if (!isOpen) return null;

  const availableTasks = allTasks?.filter(t => t?.id !== task?.id);

  const taskOptions = availableTasks?.map(t => ({
    value: t?.id,
    label: t?.name,
    description: `${t?.duration}h • ${t?.status}`
  }));

  const dependencyTypeOptions = [
    { value: 'finish-to-start', label: 'Finish to Start (FS)' },
    { value: 'start-to-start', label: 'Start to Start (SS)' },
    { value: 'finish-to-finish', label: 'Finish to Finish (FF)' },
    { value: 'start-to-finish', label: 'Start to Finish (SF)' }
  ];

  const handleAddDependency = (taskId) => {
    if (!dependencies?.includes(taskId)) {
      setDependencies([...dependencies, taskId]);
    }
  };

  const handleRemoveDependency = (taskId) => {
    setDependencies(dependencies?.filter(id => id !== taskId));
  };

  const handleSave = () => {
    onSave({
      ...task,
      dependencies,
      dependencyType,
      lagTime
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-card rounded-xl shadow-xl border border-border flex flex-col m-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Manage Dependencies</h2>
            <p className="caption text-muted-foreground mt-1">{task?.name}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            <div>
              <Select
                label="Add Predecessor Task"
                placeholder="Select a task..."
                options={taskOptions}
                value=""
                onChange={handleAddDependency}
                searchable
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Dependency Type"
                options={dependencyTypeOptions}
                value={dependencyType}
                onChange={setDependencyType}
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Lag Time (days)
                </label>
                <input
                  type="number"
                  value={lagTime}
                  onChange={(e) => setLagTime(parseInt(e?.target?.value) || 0)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {dependencies?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  Current Dependencies ({dependencies?.length})
                </label>
                <div className="space-y-2">
                  {dependencies?.map(depId => {
                    const depTask = allTasks?.find(t => t?.id === depId);
                    if (!depTask) return null;

                    return (
                      <div
                        key={depId}
                        className="flex items-center justify-between px-4 py-3 bg-muted rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Icon name="GitBranch" size={20} color="var(--color-primary)" />
                          <div>
                            <p className="text-sm font-medium text-foreground">{depTask?.name}</p>
                            <p className="caption text-muted-foreground">
                              {depTask?.duration}h • {depTask?.status}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleRemoveDependency(depId)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="var(--color-primary)" />
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Dependency Types</h4>
                  <ul className="space-y-1 caption text-muted-foreground">
                    <li>• <strong>Finish to Start (FS):</strong> Successor starts after predecessor finishes</li>
                    <li>• <strong>Start to Start (SS):</strong> Both tasks start simultaneously</li>
                    <li>• <strong>Finish to Finish (FF):</strong> Both tasks finish simultaneously</li>
                    <li>• <strong>Start to Finish (SF):</strong> Successor finishes when predecessor starts</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            iconName="Check"
            onClick={handleSave}
          >
            Save Dependencies
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DependencyMapModal;